'use client';
import React, { useEffect, useState } from 'react';
import { TDfQuery } from '@/lib/dataframeQueryApi';

import { Stack, Row } from '@/ui/primatives';
import Tag from '@/ui/Tag';
import Button from '@/ui/Button';
import SqlEditor from './SqlEditor';
import { format } from 'sql-formatter';
import * as Styled from './styled';

import SqlError from './SqlError';

const formatSql = (sql: string | undefined): { sql: string | undefined; valid: boolean } => {
  try {
    const formattedSql = format(sql || '', { language: 'postgresql', keywordCase: 'upper' });
    return { sql: formattedSql, valid: true };
  } catch (error) {
    return { sql, valid: false };
  }
};

const Heading: React.FC<{
  isGeneratedSql: boolean;
  sql: string | undefined;
  onResetSql: () => void;
  onChangeSql: (sql: string | undefined) => void;
  isSqlDirty: boolean;
}> = ({ isGeneratedSql, sql, onResetSql, onChangeSql, isSqlDirty }) => {
  return (
    <Styled.RowHeading>
      <Row gap={5} alignItems="center" justifyContent="space-between">
        <span>
          SQL Query{' '}
          {isGeneratedSql && (
            <Tag variant="success" pill>
              System generated
            </Tag>
          )}
        </span>
        <Row gap={10} alignItems="center">
          <Button.Text disabled={!isSqlDirty} small onClick={() => onResetSql()}>
            Clear changes
          </Button.Text>
          <Button.Text disabled={!isSqlDirty} small onClick={() => onChangeSql(sql)}>
            Apply changes
          </Button.Text>
        </Row>
      </Row>
    </Styled.RowHeading>
  );
};

const SqlControl: React.FC<{
  query: TDfQuery;
  schema: any;
  disabled?: boolean;
  onChangeSql: (sql: string | undefined) => void;
}> = ({ query, schema, disabled, onChangeSql }) => {
  const { sql: initialSql, valid: isSqlValid } = formatSql(query.sql || undefined);
  const hasNlp = !!query.nlp;

  const [sql, setSql] = useState<string | undefined>(initialSql);

  useEffect(() => {
    setSql(initialSql);
  }, [initialSql]);

  const onResetSql = () => {
    setSql(initialSql);
  };

  const isSqlDirty = !!(initialSql && initialSql !== sql);
  const isGeneratedSql = hasNlp && !!sql && sql === initialSql;

  return (
    <>
      <Stack alignItems="flex-end" gap={10} style={{ flex: 1 }}>
        <Row gap={0} alignItems="center">
          <Styled.QuerySegment>
            <Heading
              isGeneratedSql={isGeneratedSql}
              sql={sql}
              onResetSql={onResetSql}
              onChangeSql={onChangeSql}
              isSqlDirty={isSqlDirty}
            />
            {isSqlValid ? (
              <SqlEditor
                readOnly={disabled}
                value={sql}
                schema={schema}
                onQueryChange={(sql) => setSql(sql)}
                style={{ flex: 0.75, borderRadius: '0px' }}
              />
            ) : (
              <SqlError sql={sql} />
            )}
          </Styled.QuerySegment>
        </Row>
      </Stack>
    </>
  );
};

export default SqlControl;
